import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ArrowsClockwise, Buildings, GridFour, ChartLineUp, Database, Spinner, Info, TreeStructure, Warning } from "@phosphor-icons/react";
import { getArchitecture, refreshArchitecture } from "../lib/api";
import { MermaidDiagram } from "../components/MermaidDiagram";
import { toast } from "sonner";

const FLOWS = [
  { key: "college_master",   label: "College Master",     icon: Buildings,    color: "#6043EF" },
  { key: "seat_matrix",      label: "Seat Matrix",        icon: GridFour,     color: "#0EA5E9" },
  { key: "cutoff_allotment", label: "Cutoff / Allotment", icon: ChartLineUp,  color: "#EA580C" },
];

function ParserDetail({ p }) {
  const [flow, setFlow] = useState("college_master");
  if (!p) return null;
  const flows = p.flows || {};
  const active = flows[flow] || {};
  return (
    <div data-testid={`arch-detail-${p.module}`}>
      <div className="flex items-center gap-2 flex-wrap mb-1">
        <h2 className="text-2xl font-black tracking-tight text-[#09090B]">{p.label}</h2>
        <span className="text-[11px] font-mono text-zinc-400">{p.file}</span>
        {p.error && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-rose-600 uppercase"><Warning size={12} weight="fill" /> error</span>}
      </div>
      {p.error ? (
        <p className="text-sm text-rose-600 mt-4">{p.error}</p>
      ) : (
        <>
          {p.summary && <p className="text-sm text-zinc-600 max-w-3xl mb-4">{p.summary}</p>}
          {(p.collections_impacted || []).length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap mb-5">
              <Database size={13} weight="bold" className="text-zinc-400" />
              {p.collections_impacted.map((c) => (
                <span key={c} className="text-[10px] font-mono font-semibold bg-zinc-100 text-zinc-700 px-2 py-0.5 rounded" data-testid={`arch-coll-${c}`}>{c}</span>
              ))}
            </div>
          )}
          <div className="flex gap-1.5 mb-3 flex-wrap">
            {FLOWS.map((f) => {
              const Icon = f.icon;
              const on = flow === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFlow(f.key)}
                  data-testid={`arch-flow-${f.key}`}
                  className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-[11px] font-bold uppercase tracking-wider transition-colors ${on ? "text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
                  style={on ? { backgroundColor: f.color } : {}}
                >
                  <Icon size={14} weight="bold" /> {f.label}
                </button>
              );
            })}
          </div>
          {active.note && <p className="text-xs text-zinc-500 mb-2 italic">{active.note}</p>}
          <div className="bg-[#FAFAFA] border border-zinc-200 rounded-lg p-4">
            <MermaidDiagram chart={active.mermaid} />
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminArchitecture() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const pollRef = useRef(null);

  const load = async () => {
    try {
      const r = await getArchitecture();
      setData(r.data);
      setSelected((cur) => cur || (r.data.parsers && r.data.parsers[0]?.module) || null);
      return r.data;
    } catch { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => {
    load();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (data?.status === "running" && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const d = await load();
        if (d && d.status !== "running") { clearInterval(pollRef.current); pollRef.current = null; }
      }, 4000);
    }
  }, [data?.status]);

  const refresh = async () => {
    try {
      await refreshArchitecture();
      toast.success("Analysis started — diagrams refresh as each parser is processed");
      setData((d) => ({ ...(d || {}), status: "running", progress: { done: 0, total: (d?.parsers?.length || 14) } }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start analysis");
    }
  };

  const running = data?.status === "running";
  const parsers = data?.parsers || [];
  const prog = data?.progress;
  const activeParser = parsers.find((p) => p.module === selected) || parsers[0] || null;

  // Left-panel sections (only one for now; more will be added later)
  const sections = [{ id: "seq", title: "Sequence Diagram", icon: TreeStructure, items: parsers }];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="admin-architecture-page">
      <div className="max-w-[1300px] mx-auto px-4 sm:px-6 py-6">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-500 hover:text-[#6043EF] mb-2" data-testid="arch-back-link">
              <ArrowLeft size={13} weight="bold" /> Admin
            </Link>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tighter text-[#09090B]">Code Architecture</h1>
          </div>
          <button
            onClick={refresh}
            disabled={running}
            data-testid="arch-refresh-btn"
            className="h-10 px-5 bg-[#09090B] text-white font-bold text-xs uppercase tracking-wider rounded-lg flex items-center gap-2 disabled:opacity-60 hover:bg-[#27272A] transition-colors shrink-0"
          >
            {running ? <Spinner size={15} weight="bold" className="animate-spin" /> : <ArrowsClockwise size={15} weight="bold" />}
            {running ? `Analyzing… ${prog ? `${prog.done}/${prog.total}` : ""}` : "Refresh"}
          </button>
        </div>

        {/* Meta bar */}
        <div className="flex items-center gap-x-6 gap-y-2 flex-wrap text-xs text-zinc-500 mb-5 border border-zinc-200 rounded-lg px-4 py-2.5 bg-white" data-testid="arch-meta">
          <span>Model: <b className="text-[#09090B] font-mono">{data?.model || "gemini-2.5-flash"}</b></span>
          <span>Last refreshed: <b className="text-[#09090B]">{data?.last_refreshed_at ? new Date(data.last_refreshed_at).toLocaleString() : "never"}</b></span>
          <span>Last run cost: <b className="text-[#09090B]">${(data?.total_cost_usd || 0).toFixed(3)}</b></span>
          <span className="inline-flex items-center gap-1 text-zinc-400">
            <Info size={12} weight="bold" /> Full refresh ≈ $0.05 (Gemini 2.5 Flash @ $0.30/M in, $2.50/M out)
          </span>
        </div>

        {loading ? (
          <div className="text-center py-16 text-zinc-400 text-sm">Loading…</div>
        ) : parsers.length === 0 && !running ? (
          <div className="text-center py-16 border-2 border-dashed border-zinc-200 rounded-xl" data-testid="arch-empty">
            <p className="text-sm text-zinc-500 mb-3">No diagrams yet. Run an analysis to generate them.</p>
            <button onClick={refresh} className="h-10 px-5 bg-[#6043EF] text-white font-bold text-xs uppercase tracking-wider rounded-lg inline-flex items-center gap-2" data-testid="arch-generate-btn">
              <ArrowsClockwise size={15} weight="bold" /> Generate diagrams
            </button>
          </div>
        ) : (
          /* Split layout: left nav + right detail */
          <div className="flex gap-6 items-start">
            {/* Left panel */}
            <aside className="w-[240px] shrink-0 sticky top-4" data-testid="arch-left-panel">
              {sections.map((sec) => {
                const SIcon = sec.icon;
                return (
                  <div key={sec.id} className="mb-4">
                    <div className="flex items-center gap-1.5 px-2 mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-400">
                      <SIcon size={13} weight="bold" /> {sec.title}
                    </div>
                    <nav className="space-y-0.5">
                      {sec.items.map((p) => {
                        const on = (activeParser && p.module === activeParser.module);
                        return (
                          <button
                            key={p.module}
                            onClick={() => setSelected(p.module)}
                            data-testid={`arch-nav-${p.module}`}
                            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center justify-between gap-2 ${on ? "bg-[#6043EF] text-white" : "text-[#3f3f46] hover:bg-zinc-100"}`}
                          >
                            <span className="truncate">{p.label}</span>
                            {p.error && <Warning size={13} weight="fill" className={on ? "text-white" : "text-rose-500"} />}
                          </button>
                        );
                      })}
                    </nav>
                  </div>
                );
              })}
            </aside>

            {/* Right detail */}
            <section className="flex-1 min-w-0 bg-white border-2 border-zinc-200 rounded-xl p-5 sm:p-6" data-testid="arch-right-panel">
              {activeParser ? <ParserDetail key={activeParser.module} p={activeParser} /> : (
                <div className="text-sm text-zinc-400 py-10 text-center">Select a parser from the left.</div>
              )}
              {running && (
                <div className="flex items-center justify-center gap-2 py-6 mt-4 border-t border-zinc-100 text-sm text-zinc-500" data-testid="arch-running">
                  <Spinner size={16} weight="bold" className="animate-spin" /> Analyzing remaining parsers…
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
